import { HexCode, KisweFont, Loader } from 'kiswe-ui'
import { BoxStylingBase, BoxStylingBaseCss } from './BoxStylingBase'
import { BoxStyling } from './BoxStyling'
import type { AssetType } from '@/types/assets'
import { isAssetTypeVideoOrText, SimplecgCSS } from '@/types/layouts'
import { UNIT_PX } from '@/modules/layouts/types'

const FONT_FAMILIES_FALLBACK = [KisweFont.WorkSans, KisweFont.Helvetica, KisweFont.Arial, KisweFont.SansSerif]

const getFullFontFamily = (fontFamily?: KisweFont|string|null) => {
  const allFontFamilies = fontFamily ? [fontFamily, ...FONT_FAMILIES_FALLBACK] : FONT_FAMILIES_FALLBACK
  const allFonts = new Set(allFontFamilies)
  return [...allFonts].join(', ')
}

export type BoxStylingSimpleCgCss = BoxStylingBaseCss & Pick<
  CSSStyleDeclaration,
  'fontFamily'|'fontSize'|'fontWeight'|
  'justifyContent'|'paddingLeft'|'paddingTop'|
  'color'|'backgroundColor'|'borderRadius'
>

export class BoxStylingSimpleCg extends BoxStylingBase {
  fontFamily: string = 'Work Sans'
  fontSize: number = 16
  fontWeight: number = 400
  justifyContent: boolean = true
  paddingLeft: number = 10
  paddingTop: number = 10
  color: HexCode = '#FFFFFFFF'
  backgroundColor: HexCode|null = null
  borderRadius: number = 3

  constructor (data?: unknown) {
    super(data)
    if (!data) return
    this.fontFamily = Loader.loadString(data, 'fontFamily', this.fontFamily)
    this.fontSize = Loader.loadNumber(data, 'fontSize', this.fontSize)
    this.fontWeight = Loader.loadNumber(data, 'fontWeight', this.fontWeight)
    this.justifyContent = Loader.loadBoolean(data, 'justifyContent', this.justifyContent)
    this.paddingLeft = Loader.loadNumber(data, 'paddingLeft', this.paddingLeft)
    this.paddingTop = Loader.loadNumber(data, 'paddingTop', this.paddingTop)
    this.borderRadius = Loader.loadNumber(data, 'borderRadius', this.borderRadius)
    this.backgroundColor = Loader.loadHexCodeOrNull(data, 'backgroundColor', this.backgroundColor)
    this.color = Loader.loadHexCode(data, 'color', this.color)
  }

  override get css (): BoxStylingSimpleCgCss {
    return {
      ...super.css,
      fontFamily: getFullFontFamily(this.fontFamily),
      fontSize: this.fontSize + UNIT_PX,
      fontWeight: `${this.fontWeight}`,
      justifyContent: this.justifyContent ? 'left' : 'normal',
      paddingLeft: this.paddingLeft + UNIT_PX,
      paddingTop: this.paddingTop + UNIT_PX,
      borderRadius: this.borderRadius + UNIT_PX,
      backgroundColor: this.backgroundColor ?? 'transparent',
      color: this.color
    }
  }

  override getScaledCss (scale: number): BoxStylingSimpleCgCss {
    return {
      ...this.css,
      ...super.getScaledCss(scale)
    }
  }

  static getBoxStylingFromLegacyLayoutBox (data: unknown, types?: AssetType[]): BoxStyling {
    const styling = {
      // TODO: use ContentAlignment.Center, move classes out of layouts.ts
      alignContent: Loader.loadString(data, 'contentalign', 'center'),
      aspectRatio: Loader.loadBoolean(data, 'aspectRatio', false),
      height: Loader.loadNumber(data, 'height', 1080),
      left: Loader.loadNumber(data, 'left', 0),
      top: Loader.loadNumber(data, 'top', 0),
      width: Loader.loadNumber(data, 'width', 1920),
      zIndex: Loader.loadNumber(data, 'zIndex', 0)
    }
    return isAssetTypeVideoOrText(types) ? new BoxStylingSimpleCg(styling) : new BoxStylingBase(styling)
  }

  static mapSimpleCgCssToBoxStyling (simplecgcss: SimplecgCSS, baseBoxStyling: BoxStyling): BoxStylingSimpleCg {
    const background = simplecgcss.background ?? {}
    const text = simplecgcss.text ?? {}
    const styling = new BoxStylingSimpleCg({
      ...baseBoxStyling,
      backgroundColor: background.color,
      borderRadius: background.borderradius,
      color: text.color,
      fontSize: text.fontsize,
      fontFamily: text.font,
      justifyContent: text.ltr,
      paddingLeft: text.left,
      paddingTop: text.top,
      zIndex: baseBoxStyling.zIndex
    })
    return styling
  }
}
